import React from "react";
import Nav from "../../components/layouts/nav";
import { API_URL, URLS } from "../../_config";
import { dedupeById, replaceUrlParams, showDate } from "../../_common/functions";
import { Link, useSearchParams } from "react-router-dom";
import RightNewsList from "./RightNewsList";
import Footer from "../../components/layouts/footer";
import { useSelectedCountry } from "../../_common/hooks/authHook";
import MetaSection from "../../components/MetaSection";
import { buildCollectionPageStructuredData } from "../../utils/structuredData";

const BlogNewsList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [blogList, setBlogList] = React.useState<any>(null);
    const [newsList, setNewsList] = React.useState<any>(null);
    const [currentPage, setCurrentPage] = React.useState<number>(() => Number(searchParams.get('page') || 1));
    const [totalPages, setTotalPages] = React.useState<number>(1);
    const [newsPage, setNewsPage] = React.useState<number>(1);
    const [hasMoreNews, setHasMoreNews] = React.useState<boolean>(false);
    const [newsLoading, setNewsLoading] = React.useState<boolean>(false);
    const [blogLoading, setBlogLoading] = React.useState<boolean>(false);
    const leftColRef = React.useRef<HTMLDivElement>(null);
    const rightScrollRef = React.useRef<HTMLDivElement>(null);

    const selectedCountry: any = useSelectedCountry();

    const getBlogs = async (page: number) => {
        try {
            setBlogLoading(true);
            const response = await fetch(API_URL.BLOG_NEWS.LIST(selectedCountry?.id || null, page));
            const resp = await response.json();
            setBlogList(resp.data?.blog?.data || []);
            setTotalPages(resp.data?.blog?.last_page || 1);
        } catch (error) {
            console.log('Error occurred while making the API request.');
            setBlogList([]);
        } finally {
            setBlogLoading(false);
        }
    };

    const getInitialNews = async () => {
        try {
            const response = await fetch(API_URL.BLOG_NEWS.NEWS_LIST(selectedCountry?.id || null, 1));
            const resp = await response.json();
            const items = resp.data?.latest_news?.data || [];
            setNewsList(items);
            setNewsPage(1);
            setHasMoreNews(items.length > 0 && 1 < (resp.data?.latest_news?.last_page || 1));
        } catch (error) {
            console.log('Error occurred while making the API request.');
            setNewsList([]);
        }
    };

    const loadMoreNews = async () => {
        if (newsLoading || !hasMoreNews) return;
        setNewsLoading(true);
        try {
            const nextPage = newsPage + 1;
            const response = await fetch(API_URL.BLOG_NEWS.NEWS_LIST(selectedCountry?.id || null, nextPage));
            const resp = await response.json();
            const newItems = resp.data?.latest_news?.data || [];
            setNewsList((prev: any[]) => dedupeById([...prev, ...newItems]));
            setNewsPage(nextPage);
            if (newItems.length === 0 || nextPage >= (resp.data?.latest_news?.last_page || 1)) {
                setHasMoreNews(false);
            }
        } catch (error) {
            console.log('Error loading more news.');
        } finally {
            setNewsLoading(false);
        }
    };

    React.useEffect(() => {
        getInitialNews();
    }, [selectedCountry]);

    // Sync right sidebar height to left blog grid height
    React.useEffect(() => {
        if (!leftColRef.current || !rightScrollRef.current) return;
        const syncHeight = () => {
            requestAnimationFrame(() => {
                if (leftColRef.current && rightScrollRef.current) {
                    rightScrollRef.current.style.maxHeight = `${leftColRef.current.offsetHeight}px`;
                }
            });
        };
        syncHeight();
        const resizeObserver = new ResizeObserver(syncHeight);
        resizeObserver.observe(leftColRef.current);
        return () => resizeObserver.disconnect();
    }, [blogList]);

    React.useEffect(() => {
        getBlogs(currentPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage, selectedCountry]);

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    const getPaginationPages = (): (number | '...')[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages: (number | '...')[] = [1];
        const left = Math.max(2, currentPage - 1);
        const right = Math.min(totalPages - 1, currentPage + 1);
        if (left > 2) pages.push('...');
        for (let i = left; i <= right; i++) pages.push(i);
        if (right < totalPages - 1) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    const collectionItems = (blogList || []).map((item: any) => ({
        name: item?.title,
        url: replaceUrlParams(URLS.BLOG_NEWS.BLOG_DETAILS, { slug: item?.slug }),
    }));

    return (
        <div className='bg-white'>
            {/* <MetaSection
                title="Blog & News — Plan Crafter"
                desc="Latest blogs and news from Plan Crafter."
                url={URLS.BLOG_NEWS.LIST}
                type="website"
                structuredData={buildCollectionPageStructuredData("Blog & News", URLS.BLOG_NEWS.LIST, collectionItems)}
            /> */}
            <Nav />
            <div className="subscription">
                <div className="container">
                    <div className="banner-text latest_news_title">
                        <h2 data-aos="fade-up" className="text-left pb-1">Blogs </h2>
                    </div>
                    <div className="row">
                        <div ref={leftColRef} className="col-lg-8 col-md-12">
                            <div className="row">
                                {
                                    blogLoading && blogList === null && <div className="text-center my-5">Loading...</div>
                                }
                                {
                                    !blogLoading && blogList?.length === 0 && <div className="text-center">No Blogs Found</div>
                                }
                                {
                                    blogList?.map((item: any, index: number) => {
                                        return <div key={`list__${index}`} className="col-lg-4 col-md-4">
                                            <div className="news_liatitem">
                                                <div className="news_listimage">
                                                    <Link to={replaceUrlParams(URLS.BLOG_NEWS.BLOG_DETAILS, { slug: item?.slug })}>
                                                        <img src={item?.blog_thumb?.original} alt={item?.title} />
                                                    </Link>
                                                    <div className="news_date">
                                                        <span className="news_datetxt">{showDate(item?.published_date, 'DD')}</span>
                                                        <span className="news_datemonth">{showDate(item?.published_date, 'MMM')}</span>
                                                    </div>
                                                </div>
                                                <Link to={replaceUrlParams(URLS.BLOG_NEWS.BLOG_DETAILS, { slug: item?.slug })}>
                                                    <h3 className="news_title">{item?.title}</h3>
                                                </Link>
                                                <p>{item?.short_description}</p>
                                                <div className="more_btn">
                                                    <Link to={replaceUrlParams(URLS.BLOG_NEWS.BLOG_DETAILS, { slug: item?.slug })}>Read More</Link>
                                                </div>
                                            </div>
                                        </div>
                                    })
                                }
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <ul className="pagination" aria-label="Blog pagination">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                aria-label="Previous page"
                                            >
                                                &laquo; Previous
                                            </button>
                                        </li>
                                        {getPaginationPages().map((page, idx) =>
                                            page === '...'
                                                ? <li key={`ellipsis_${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
                                                : <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page as number)}
                                                        aria-label={`Page ${page}`}
                                                        aria-current={currentPage === page ? 'page' : undefined}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                        )}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                aria-label="Next page"
                                            >
                                                Next &raquo;
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="col-lg-4 col-md-12" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div
                                ref={rightScrollRef}
                                style={{ overflowY: 'auto', flex: 1 }}
                            >
                                <RightNewsList
                                    newsList={newsList}
                                    title='News'
                                    onLoadMore={loadMoreNews}
                                    hasMore={hasMoreNews}
                                    isLoading={newsLoading}
                                    scrollContainerRef={rightScrollRef}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
export default BlogNewsList;
